// React
import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import useHandleLoader from '../../../msreact-kit/hooks/useHandleLoader';

// Lib
import i18next from 'i18next';

// Components
import {
  Breadcrumb,
  PrimaryButton,
  SecondaryButton,
  TextInput,
  Dropdown as SingleSelect
} from '../../../msreact-kit/packages/html-components';
import {
  getColourNode,
  orderStatus as orderStatusUtils
} from '../../../shared/shared-function/helperFunctions';
import ProductDetails from './product-detail';
import OrderStatus from './order-status';
import OrderOverview from './order-overview';
import DynamicPopUp from './dynamic-popup';
import DynamicComment from './dynamic-comment';
import ConsumptionPosting from './consumption-posting';
import SidebarContentWrapper from '../../BaseLayout/SidebarContainer';
import Footer from '../../BaseLayout/footer';
import { Dialog } from '@mobilitystore/components-interface';

// Actions
import {
  TENANT,
  IS_SHIPMENT_MANAGEMENT_ENABLED
} from '../../../actions/configuration';
import {
  getComments,
  getOrderStatus,
  getOrderSummary,
  resetOrderState,
  getPricingPolicies,
  addShippingRemark
} from '../../../actions/api/OrderSummaryAction';
import { getInvoiceDetails, postPaymentStatus } from '../../../actions/order-service-action';
import { confirmSellerApproval } from '../../../actions/order-fulfilment-service';
import { getContractInfo } from '../../../actions/api/ConsumptionPostingAction';
import { getPickupAddress } from '../../../actions/user-service-action';
import {
  createShipment,
  getShipmentStatus
} from '../../../actions/shipment-service-action';
import { getProductDetailedInfo } from '../../../actions/api/SolutionAction';

// Utils
import Loader from '../../../components/Shared/Utils/Loader';
import {
  getMappedConsignmentFormData,
  updateConsignmentFormFields,
  blurConsignmentFormFields,
  getConsignmentButtons,
  setConsignmentFormPayload,
  isConsignmentFormValid,
  updateConsignmentFormFieldsWithInvoice
} from './utils';

// Data
import idConstants, { REGION } from '../../../data/idConstants';
import { accessRoles } from '../../../data/appConstants';
import { consignmentDynamicFormFields } from './data';

// Css
import './index.scss';

const OrderSummary = ({ ...props }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [handleLoader] = useHandleLoader();
  const { productOrderDetailId } = useParams();
  let [currentOrderStage, setCurrentOrderStage] = useState({});
  const [showConfirmOrder, setShowConfirmOrder] = useState(false);
  const [showRejectOrder, setShowRejectOrder] = useState(false);
  const [comment, setComment] = useState(false);
  const [shippingRemark, setShippingRemark] = useState(false);
  const [consignmentRemark, setConsignmentRemark] = useState(false);
  const [consignmentFormFields, setConsignmentFormFields] = useState([]);
  const [pickupLocationDetails, setPickupLocationDetails] = useState([]);
  const [transitDetail, setTransitDetail] = useState(false);
  const [podDetails, setPodDetails] = useState(false);
  const [filteredPricingPolicies, setFilteredPricingPolicies] = useState(null);
  const [shipmentStatus, setShipmentStatus] = useState(null);
  const [invoiceNetPrice, setInvoiceNetPrice] = useState(null);
  const [showPopUp, setShowPopUp] = useState(false);
  const [loaderForTimeout, setLoaderForTimeout] = useState(false);
  const {
    orderStatus,
    statusChange: statusChangeValue,
    orderStatusValue,
    paymentStatusChange: paymentStatusChangeValue,
    postComment,
    orderDescription,
    billingAddress,
    isCancellable,
    errorState,
    pricingPolicies
  } = useSelector((state) => {
    return state.OrderSummaryReducer;
  });
  const { contractData } = useSelector((state) => {
    return state.ConsumptionReducer;
  });
  const { loader, user, content, userCompanyStatus, hardwareProductDetail } =
    useSelector((state) => {
      return {
        loader: state.LoaderReducer.loader,
        user: state.loginReducer.user,
        content: state.ContentReducer.content,
        userCompanyStatus: state.CompanyDetailsReducer.userCompanyStatus,
        hardwareProductDetail: state.HardwareProductReducer.hardwareProductDetail
      };
    });

  const dialogButtons = [
    {
      label: i18next.t('ORDER_MANAGEMENT.DYNAMIC_POPUP.POPUP.CANCEL'),
      mode: 'secondary',
      onClick: () => {
        setShowPopUp(false);
      }
    },
    {
      label: i18next.t('ORDER_MANAGEMENT.DYNAMIC_POPUP.POPUP.CONFIRM'),
      mode: 'primary',
      onClick: () => {
        dispatch(
          postPaymentStatus(
            orderDescription?.productOrderDto?.productOrderId,
            orderDescription?.orderSubscriptionMap?.tenantId,
            paymentMode,
            transactionId
          )
        );
      }
    }
  ];

  const [paymentMode, setPaymentMode] = useState('');
  const [transactionId, setTransactionId] = useState('');

  const isTransformCategory =
    orderDescription?.productDetailsDto?.category?.id ===
    idConstants.transform.categoryId;
  const isHardwareCategory =
    orderDescription?.productDetailsDto?.category?.id ===
    idConstants.hardware.categoryId;
  const isReadyToShipStatus =
    currentOrderStage?.labelTwo ===
    i18next.t('ORDER_MANAGEMENT.ORDER_STATUS.READY_TO_SHIP');
  const isReseller = user?.roles?.includes(accessRoles.reseller);
  const isInvoiceGenerated =
    orderDescription?.orderSubscriptionMap?.invoiceGenerated;
  const isReadyToShipBtnDisabled =
    isReseller && !isInvoiceGenerated && isReadyToShipStatus && isHardwareCategory;
  const productId = orderDescription?.productOrderDto?.productId;
  const categoryId = orderDescription?.productDetailsDto?.category?.id;

  const isPlatformFullfilledByPlatform =
    hardwareProductDetail?.productDetailedInfo?.productDetail?.genericInformation
      ?.platformFulfillment?.value ===
    idConstants.platformFulfillment.fullfilledByPlatform;

  // Adding IN region check as this feature is only for IN region Resellers
  const isIndiaRegion = process.env.REACT_APP_REGION === REGION.IN.CODE;
  const isPlatformFullfillmentFlow =
    IS_SHIPMENT_MANAGEMENT_ENABLED &&
    isReseller &&
    isPlatformFullfilledByPlatform &&
    isIndiaRegion;

  const shipmentManagementContent =
    content?.components?.user_management?.order_fulfillment?.shipment_management;
  useEffect(() => {
    if (pricingPolicies?.length > 0) {
      const pricingPoliciesDetails = pricingPolicies?.find(
        (planDetails) =>
          planDetails.planId === orderDescription?.productOrderDto?.policyId
      );
      setFilteredPricingPolicies(pricingPoliciesDetails);
    }
  }, [pricingPolicies, orderDescription]);

  // Fetch invoice details to get netPrice when planCost is missing or zero
  useEffect(() => {
    const planCost = Number(filteredPricingPolicies?.planCost);
    const productOrderId = orderDescription?.productOrderDto?.productOrderId;
    const tenantId = orderDescription?.orderSubscriptionMap?.tenantId;
    // Uzos tenant orders should not fetch invoice details from this api
    const isUzosTenant = tenantId === TENANT.UZOS;
    if ((Number.isNaN(planCost) || planCost === 0) && productOrderId && tenantId && !isUzosTenant) {
      if (invoiceNetPrice == null) {
        dispatch(getInvoiceDetails(productOrderId, tenantId)).then((response) => {
          if (typeof response?.netAmount !== 'undefined') {
            setInvoiceNetPrice(response?.netAmount);
          }
        });
      }
    } else {
      if (invoiceNetPrice != null) setInvoiceNetPrice(null);
    }
  }, [
    filteredPricingPolicies,
    orderDescription?.productOrderDto?.productOrderId,
    orderDescription?.orderSubscriptionMap?.tenantId
  ]);

  useEffect(() => {
    handleLoader();
  }, []);

  useEffect(() => {
    userCompanyStatus?.companyId &&
      isPlatformFullfillmentFlow &&
      dispatch(getPickupAddress(userCompanyStatus.companyId)).then((response) => {
        setPickupLocationDetails(response);
      });
  }, [userCompanyStatus?.companyId, isPlatformFullfillmentFlow]);

  useEffect(() => {
    isReseller &&
      productId &&
      categoryId &&
      dispatch(getProductDetailedInfo(productId, categoryId));
  }, [isReseller, productId, categoryId]);

  useEffect(() => {
    if (errorState) {
      history.push('/dashboard/order-management');
    }
    return () => {
      dispatch(resetOrderState());
    };
  }, [errorState]);

  useEffect(() => {
    if (productOrderDetailId) {
      dispatch(getOrderSummary(user, productOrderDetailId));
      dispatch(getContractInfo(props.location?.state?.dcsOrderId));
    }
  }, [productOrderDetailId]);

  useEffect(() => {
    if (orderDescription?.productOrderDto?.productId) {
      dispatch(getPricingPolicies(orderDescription?.productOrderDto?.productId));
    }
  }, [orderDescription?.productOrderDto?.productId]);

  const getOrderStatusDetails = () => {
    dispatch(getOrderStatus(productOrderDetailId, orderDescription, pricingPolicies)).then(() => {
      dispatch(getComments(productOrderDetailId));
    });
  };

  useEffect(() => {
    // Skip the initial call when pricingPolicies is empty
    if (pricingPolicies?.length === 0) return;
    if (orderStatusValue === orderStatusUtils.ORDER_CONFIRMED && filteredPricingPolicies?.planDetails?.paymentModePref === 'APPROVAL_PREPAID') {
      setLoaderForTimeout(true);
      setTimeout(() => {
        getOrderStatusDetails();
        setLoaderForTimeout(false);
      }, 4000);
    } else {
      getOrderStatusDetails();
    }
  }, [statusChangeValue, postComment, pricingPolicies, orderStatusValue]);

  useEffect(() => {
    setShowPopUp(false);
    if (pricingPolicies?.length === 0) return;
    dispatch(getOrderSummary(user, productOrderDetailId));
    dispatch(getOrderStatus(productOrderDetailId, orderDescription, pricingPolicies)).then(() => {
      dispatch(getComments(productOrderDetailId));
    });
  }, [paymentStatusChangeValue, pricingPolicies]);

  useEffect(() => {
    if (
      isPlatformFullfillmentFlow &&
      (currentOrderStage?.status === orderStatusUtils.INTRANSIT ||
        currentOrderStage?.status === orderStatusUtils.DELIVERED) &&
      orderDescription?.productOrderDto?.productOrderId
    ) {
      dispatch(getShipmentStatus(orderDescription.productOrderDto.productOrderId))
        .then((response) => {
          setShipmentStatus(response);
        })
        .catch(() => {
          setShipmentStatus(null);
        });
    } else {
      setShipmentStatus(null);
    }
  }, [
    isPlatformFullfillmentFlow,
    currentOrderStage?.status,
    orderDescription?.productOrderDto?.productOrderId
  ]);

  const getCTAResult = (orderStatus, getCurrentStatusIndex, tenantId) => {
    const { currentNode } = getCurrentStatusIndex;

    if (currentNode >= orderStatus.length - 1) {
      return orderStatus[currentNode];
    }
    const nextStatus = orderStatus[currentNode + 1].status;
    switch (nextStatus) {
      case orderStatusUtils.ORDER_CONFIRMED:
        orderStatus['labelTwo'] = i18next.t(
          'ORDER_MANAGEMENT.ORDER_STATUS.CONFIRM_ORDER'
        );
        orderStatus['labelKeyTwo'] = 2;
        break;
      case orderStatusUtils.READY_TO_SHIP:
        orderStatus['labelTwo'] = i18next.t(
          'ORDER_MANAGEMENT.ORDER_STATUS.READY_TO_SHIP'
        );
        orderStatus['labelKeyTwo'] = 3;
        break;
      case orderStatusUtils.INTRANSIT:
        orderStatus['labelTwo'] = i18next.t(
          'ORDER_MANAGEMENT.ORDER_STATUS.ADD_TRANSIT_DETAILS'
        );
        orderStatus['labelKeyTwo'] = 4;
        break;
      case orderStatusUtils.DELIVERED:
        orderStatus['labelTwo'] =
          tenantId == TENANT.PSIN
            ? i18next.t('ORDER_MANAGEMENT.ORDER_STATUS.SUBMIT_CLIENT_SECRET_ID')
            : i18next.t('ORDER_MANAGEMENT.ORDER_STATUS.SUBMIT_POD');
        orderStatus['labelKeyTwo'] = 5;
        break;
      case orderStatusUtils.COMPLETED:
        orderStatus['labelTwo'] = i18next.t('DYNAMIC_FORM.SHARED.APPROVE');
        orderStatus['labelKeyTwo'] = 6;
        break;
    }
    orderStatus.status = orderStatus[currentNode].status;
    orderStatus.nextStatus = nextStatus;

    return orderStatus;
  };

  useEffect(() => {
    if (
      orderStatus?.length > 0 &&
      orderDescription?.orderSubscriptionMap?.tenantId
    ) {
      const getCurrentStatusIndex = getColourNode(orderStatus);
      const ctaResult = getCTAResult(
        orderStatus,
        getCurrentStatusIndex,
        orderDescription?.orderSubscriptionMap?.tenantId
      );
      setCurrentOrderStage(ctaResult);
    }
  }, [orderStatus, orderDescription?.orderSubscriptionMap?.tenantId]);

  const handleConfirm = () => {
    setShowConfirmOrder(() => true);
  };

  const handleReject = () => {
    setShowRejectOrder(() => true);
  };

  const handleOpenComment = () => {
    setComment(() => true);
  };

  const handleShippingRemark = () => {
    setShippingRemark(() => true);
  };

  const handleConsignmentRemark = () => {
    if (userCompanyStatus?.companyId) {
      dispatch(getPickupAddress(userCompanyStatus.companyId)).then(
        (pickupResponse) => {
          setPickupLocationDetails(pickupResponse);
          if (
            orderDescription?.productOrderDto?.productOrderId &&
            orderDescription?.orderSubscriptionMap?.tenantId
          ) {
            dispatch(
              getInvoiceDetails(
                orderDescription?.productOrderDto?.productOrderId,
                orderDescription?.orderSubscriptionMap?.tenantId
              )
            ).then((invoiceResponse) => {
              const updatedFields = updateConsignmentFormFieldsWithInvoice(
                getMappedConsignmentFormData(
                  consignmentDynamicFormFields,
                  pickupResponse
                ),
                invoiceResponse
              );
              setConsignmentFormFields(updatedFields);
              setConsignmentRemark(true);
            });
          } else {
            setConsignmentFormFields(
              getMappedConsignmentFormData(
                consignmentDynamicFormFields,
                pickupResponse
              )
            );
            setConsignmentRemark(true);
          }
        }
      );
    } else {
      setConsignmentFormFields(mappedConsignmentFormDetails);
      setConsignmentRemark(true);
    }
  };

  const handleTransitDetails = () => {
    setTransitDetail(() => true);
  };

  const handlePODDetails = () => {
    setPodDetails(() => true);
  };

  const handleSellerApproval = () => {
    dispatch(
      confirmSellerApproval(
        orderDescription?.productOrderDto?.productOrderDetailId,
        orderDescription
      )
    );
  };

  const handleDisabled = (label) => {
    if (
      label === i18next.t('ORDER_MANAGEMENT.ORDER_STATUS.READY_TO_SHIP') &&
      isReseller &&
      !isInvoiceGenerated
    ) {
      return true;
    }

    const platformDisabledLabels = [
      i18next.t('ORDER_MANAGEMENT.ORDER_STATUS.ADD_TRANSIT_DETAILS'),
      i18next.t('ORDER_MANAGEMENT.ORDER_STATUS.SUBMIT_POD')
    ];

    if (platformDisabledLabels.includes(label) && isPlatformFullfillmentFlow) {
      return true;
    }
    return false;
  };

  const handleOrderStatusButton = () => {
    if (
      !isTransformCategory &&
      currentOrderStage?.nextStatus ===
      i18next.t('ORDER_MANAGEMENT.ORDER_STATUS.COMPLETED').toUpperCase()
    ) {
      return;
    } else {
      return (
        <PrimaryButton
          label={currentOrderStage?.labelTwo}
          disabled={handleDisabled(currentOrderStage?.labelTwo)}
          onClick={actionCalCulator(currentOrderStage?.labelKeyTwo)}
        />
      );
    }
  };

  const handleClose = () => {
    if (showConfirmOrder) setShowConfirmOrder(() => false);
    if (showRejectOrder) setShowRejectOrder(() => false);
    if (comment) setComment(() => false);
    if (shippingRemark) setShippingRemark(() => false);
    if (consignmentRemark) setConsignmentRemark(() => false);
    if (transitDetail) setTransitDetail(() => false);
    if (podDetails) setPodDetails(() => false);
  };

  const actionCalCulator = (label) => {
    switch (label) {
      case 1:
        return handleReject;
      case 2:
        return handleConfirm;
      case 3:
        return isPlatformFullfillmentFlow
          ? handleConsignmentRemark
          : handleShippingRemark;
      case 4:
        return handleTransitDetails;
      case 5:
        return handlePODDetails;
      case 6:
        return handleSellerApproval;
    }
  };

  const getBreadCrumb = () => {
    const isOrderHistory = history?.location?.state?.isHistory;
    const returnSearch = history?.location?.state?.returnSearch || '';
    const breadcrumbs = [
      {
        title: isOrderHistory
          ? i18next.t('DYNAMIC_FORM.NAVIGATION_OPTIONS.ORDER_HISTORY')
          : i18next.t('DYNAMIC_FORM.NAVIGATION_OPTIONS.MY_ORDERS'),
        to: isOrderHistory
          ? `/dashboard/order-history${returnSearch}`
          : `/dashboard/order-management${returnSearch}`
      },
      {
        title: i18next.t('ORDER_MANAGEMENT.ORDER_SUMMARY.HEADER.ORDER_SUMMARY'),
        to: '/dashboard/order-management/order-details'
      }
    ];
    return breadcrumbs;
  };

  const mappedConsignmentFormDetails = getMappedConsignmentFormData(
    consignmentDynamicFormFields,
    pickupLocationDetails
  );

  const handleConsignmentFormChange = (data) => {
    setConsignmentFormFields((prevSections) =>
      updateConsignmentFormFields(prevSections, data)
    );
  };

  const handleConsignmentFormBlur = (data) => {
    setConsignmentFormFields((prevFields) =>
      blurConsignmentFormFields(prevFields, data)
    );
  };

  const handleConsignmentFormSubmit = () => {
    const payload = setConsignmentFormPayload(
      consignmentFormFields,
      pickupLocationDetails,
      billingAddress,
      orderDescription
    );
    payload &&
      dispatch(createShipment(payload)).then((response) => {
        if (response) {
          dispatch(addShippingRemark(productOrderDetailId, '', orderDescription));
          setConsignmentFormFields([]);
          setConsignmentRemark(false);
        }
      });
  };

  const consignmentButtons = getConsignmentButtons(
    handleClose,
    handleConsignmentFormSubmit
  );
  const shipmentFlow = isPlatformFullfillmentFlow && shipmentStatus;

  const getShipmentStatusDescription = (status) => {
    let description = '';
    let className = '';
    switch (status) {
      case 'INTRANSIT':
        description = shipmentManagementContent?.status?.intransit;
        className = 'shipment-status__intransit';
        break;
      case 'OUT_FOR_DELIVERY':
        description = shipmentManagementContent?.status?.out_for_delivery;
        className = 'shipment-status__out-for-delivery';
        break;
      case 'DELIVERY_FAILED':
        description = shipmentManagementContent?.status?.delivery_failed;
        className = 'shipment-status__delivery-failed';
        break;
      case 'DELAYED':
        description = shipmentManagementContent?.status?.delayed;
        className = 'shipment-status__delayed';
        break;
      case 'DELIVERED':
        description = shipmentManagementContent?.status?.delivered;
        className = 'shipment-status__delivered';
        break;
      default:
        description = status;
        className = 'shipment-status__other';
    }
    return { description, className };
  };

  return (
    <SidebarContentWrapper className='order-summary-sidebar order-summary-details'>
      {(loader || loaderForTimeout) && <Loader />}
      <div className='order-summary-wrapper'>
        <div
          className={
            showConfirmOrder ||
              showRejectOrder ||
              comment ||
              shippingRemark ||
              consignmentRemark ||
              transitDetail ||
              podDetails
              ? 'popup-open'
              : 'popup-close'
          }
        >
          <DynamicPopUp
            productOrderId={orderDescription?.productOrderDto?.productOrderId}
            commentId={orderDescription?.productOrderDto?.productOrderId}
            productOrderDetailId={productOrderDetailId}
            transitDetail={transitDetail}
            shippingRemark={shippingRemark}
            consignmentRemark={consignmentRemark}
            comment={comment}
            onClose={handleClose}
            showConfirmOrder={showConfirmOrder}
            showRejectOrder={showRejectOrder}
            showPODDetails={podDetails}
            isCancellable={isCancellable}
            tenantId={orderDescription?.tenantId}
            consignmentDynamicFormFields={consignmentFormFields}
            consignmentButtons={consignmentButtons}
            handleInputChange={handleConsignmentFormChange}
            handleBlur={handleConsignmentFormBlur}
            isConsignmentFormValid={isConsignmentFormValid}
          />
        </div>
        <Breadcrumb items={getBreadCrumb()} className='bread-crumb__arrow' />
        <h5 className='order-summary-page-title'>
          {i18next.t('ORDER_MANAGEMENT.ORDER_SUMMARY.HEADER.ORDER_SUMMARY')}
        </h5>
        {orderDescription &&
          billingAddress &&
          Object.keys(orderDescription)?.length !== 0 &&
          (Object.keys(billingAddress)?.length > 0 ||
            !orderDescription?.buyerDetails?.companyId) && (
            <>
              <div className='order-summary-container'>
                <div className='order-summary'>
                  <div className='order-summary__body'>
                    <OrderOverview
                      currentOrderStage={currentOrderStage}
                      invoiceNetPrice={invoiceNetPrice}
                    />
                  </div>
                </div>
              </div>
              <div className='order-summary-container'>
                <div className='order-summary'>
                  <div className='order-summary__body'>
                    <div className='order-summary__body__flex-divider'>
                      <ProductDetails invoiceNetPrice={invoiceNetPrice} />
                      <OrderStatus
                        productOrderId={
                          orderDescription?.productOrderDto?.productOrderId
                        }
                        isPlatformFullfillmentFlow={isPlatformFullfillmentFlow}
                        tenantId={orderDescription?.orderSubscriptionMap?.tenantId}
                      />
                    </div>
                  </div>
                  {orderStatus?.length > 0 ? (
                    <div className='order-summary__footer'>
                      <div
                        className={`order-summary__navigation-buttons ${isReadyToShipBtnDisabled || shipmentFlow
                          ? 'space-between'
                          : ''
                          }`}
                      >
                        {isReadyToShipBtnDisabled && (
                          <div className='ready-to-ship-info'>
                            <i className='boschicon-bosch-ic-alert-info info-style-icon'></i>
                            <p>
                              {
                                content?.components?.user_management
                                  ?.order_fulfillment?.ready_to_ship?.text_1
                              }
                            </p>
                          </div>
                        )}

                        {shipmentFlow &&
                          (() => {
                            const status = shipmentStatus?.event?.status;
                            const { description, className } =
                              getShipmentStatusDescription(status);
                            return (
                              <div
                                className={`shipment-status-info shipment-status ${className}`}
                              >
                                <div className='shipment-status-left'>
                                  <i className='boschicon-bosch-ic-alert-info info-style-icon'></i>
                                  <span className='shipment-status-desc'>{`${shipmentManagementContent?.shipment_update} ${description}`}</span>
                                </div>
                                {shipmentStatus?.eta && (
                                  <div className='shipment-status-eta'>
                                    <span>{`${shipmentManagementContent?.eta}: ${shipmentStatus.eta}`}</span>
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                        <div className='order-summary__navigation-buttons__right-wrapper'>
                          {(currentOrderStage?.nextStatus ===
                            orderStatusUtils.PAYMENT || currentOrderStage?.nextStatus ===
                            orderStatusUtils.POST_PAYMENT) &&
                            orderDescription?.orderSubscriptionMap?.tenantId === TENANT.UZOS && (
                              <SecondaryButton
                                label={i18next.t(
                                  'ORDER_MANAGEMENT.DYNAMIC_POPUP.MARK_PAYMENT_COMPLETE'
                                )}
                                onClick={() => setShowPopUp(true)}
                              />
                            )}
                          {currentOrderStage?.nextStatus !==
                            orderStatusUtils.ORDER_CREATED &&
                            currentOrderStage?.nextStatus !==
                            orderStatusUtils.ORDER_CONFIRMED && (
                              <SecondaryButton
                                label={i18next.t(
                                  'ORDER_MANAGEMENT.DYNAMIC_POPUP.ADD_COMMENT'
                                )}
                                onClick={handleOpenComment}
                                className={
                                  currentOrderStage?.orderStatus === 'Reject' &&
                                  'label-reject'
                                }
                              />
                            )}
                          {isCancellable === 'true' &&
                            currentOrderStage?.nextStatus !== orderStatusUtils.DELIVERED &&
                            (orderDescription?.orderSubscriptionMap?.tenantId !== TENANT.UZOS ||
                              orderDescription?.orderSubscriptionMap?.invoicePaymentStatus !== 'PAYMENT_CAPTURED') && (
                              <SecondaryButton
                                label={i18next.t('DYNAMIC_FORM.LABEL.REJECT')}
                                onClick={handleReject}
                                className={'label-reject'}
                              />
                            )}
                          {currentOrderStage?.labelTwo && handleOrderStatusButton()}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              {orderDescription?.orderSubscriptionMap?.productCategoryId ===
                idConstants.solutions.categoryId &&
                currentOrderStage.status === orderStatusUtils.BUYER_APPROVED &&
                filteredPricingPolicies?.consumption !==
                idConstants.pricing.notApplicable && (
                  <div className='order-summary-container'>
                    <div className='order-summary'>
                      <div className='order-summary__body'>
                        <ConsumptionPosting
                          contractData={contractData}
                          orderDescription={orderDescription}
                          pricingPolicies={filteredPricingPolicies}
                        />
                      </div>
                    </div>
                  </div>
                )}
              <DynamicComment
                isPlatformFullfillmentFlow={isPlatformFullfillmentFlow}
              />
              {
                <Dialog
                  status='info'
                  title={i18next.t('ORDER_MANAGEMENT.DYNAMIC_POPUP.POPUP.TITLE')}
                  show={showPopUp}
                  showPopup={showPopUp}
                  showCloseIcon={true}
                  buttons={dialogButtons}
                  onClose={() => setShowPopUp(false)}
                >
                  <SingleSelect
                    options={[
                      {
                        label: i18next.t('ORDER_MANAGEMENT.DYNAMIC_POPUP.PAYMENT_MODE_BANK_TRANSFER'),
                        id: 'bank_transfer',
                        name: 'Bank transfer'
                      },
                      {
                        label: i18next.t('ORDER_MANAGEMENT.DYNAMIC_POPUP.PAYMENT_MODE_UPI'),
                        id: 'upi',
                        name: 'UPI'
                      },
                      {
                        label: i18next.t('ORDER_MANAGEMENT.DYNAMIC_POPUP.PAYMENT_MODE_CARD'),
                        id: 'card',
                        name: 'Credit/Debit card'
                      },
                      {
                        label: i18next.t('ORDER_MANAGEMENT.DYNAMIC_POPUP.PAYMENT_MODE_MANUAL'),
                        id: 'manual',
                        name: 'Manual payment'
                      }
                    ]}
                    placeHolder={i18next.t(
                      'ORDER_MANAGEMENT.DYNAMIC_POPUP.PAYMENT_MODE_PLACEHOLDER'
                    )}
                    isRequired={true}
                    placeHolderDisabled={true}
                    displayName={i18next.t(
                      'ORDER_MANAGEMENT.DYNAMIC_POPUP.PAYMENT_MODE'
                    ) + ' ' + `(${i18next.t('ORDER_MANAGEMENT.DYNAMIC_POPUP.OPTIONAL')})`}
                    name='paymentMode'
                    value={paymentMode}
                    handleChange={(event) => {
                      setPaymentMode(event.value.name);
                    }}
                  />
                  <br />
                  <TextInput displayName={i18next.t(
                    'ORDER_MANAGEMENT.DYNAMIC_POPUP.TRANSACTION_ID'
                  ) + ' ' + `(${i18next.t('ORDER_MANAGEMENT.DYNAMIC_POPUP.OPTIONAL')})`}
                    placeHolder={i18next.t(
                      'ORDER_MANAGEMENT.DYNAMIC_POPUP.TRANSACTION_ID_PLACEHOLDER'
                    )}
                    required={false}
                    value={transactionId}
                    onChange={(event) => { setTransactionId(event.target.value); }}
                    type='text' />
                  <p className='order-summary-popup-desc'>
                    {i18next.t('ORDER_MANAGEMENT.DYNAMIC_POPUP.POPUP.DESC')}
                  </p>
                </Dialog>
              }
            </>
          )}
        <Footer customClassName='d-lg-none' />
      </div>
    </SidebarContentWrapper>
  );
};

export default OrderSummary;
